import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { WishlistProvider } from './context/WishlistContext';
import { OrderAvailabilityProvider } from './context/OrderAvailabilityContext';
import { Toaster } from './components/ui/sonner';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import LoadingSpinner from './components/common/LoadingSpinner';
import LocationPermissionModal from './components/common/LocationPermissionModal';
import CountrySelector from './components/common/CountrySelector';
import ScrollToTop from './components/common/ScrollToTop';
import Canonical from './components/Canonical';
import './index.css';

// Lazy load pages for better performance
const Home = lazy(() => import('./pages/Home'));
const Shop = lazy(() => import('./pages/Shop'));
const Origins = lazy(() => import('./pages/Origins'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Register = lazy(() => import('./pages/Register'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const BuyerDashboard = lazy(() => import('./pages/BuyerDashboard'));
const SellerDashboard = lazy(() => import('./pages/SellerDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const SellerStore = lazy(() => import('./pages/SellerStore'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const StaticPage = lazy(() => import('./pages/StaticPage'));
const SellerRegister = lazy(() => import('./pages/SellerRegister'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ReviewOrder = lazy(() => import('./pages/ReviewOrder'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));
const FeaturedBiscuits = lazy(() => import('./pages/FeaturedBiscuits'));
const FeaturedShops = lazy(() => import('./pages/FeaturedShops'));
const Categories = lazy(() => import('./pages/Categories'));
const NewArrivals = lazy(() => import('./pages/NewArrivals'));
const SellersByLocation = lazy(() => import('./pages/SellersByLocation'));
const BecomeASeller = lazy(() => import('./pages/BecomeASeller'));
const AwardsPage = lazy(() => import('./pages/AwardsPage'));
const Messages = lazy(() => import('./pages/Messages'));
const StakeholderPage = lazy(() => import('./pages/StakeholderPage'));
const BiscuitsByCountry = lazy(() => import('./pages/BiscuitsByCountry'));
const BiscuitPage = lazy(() => import('./pages/BiscuitPage'));
const BiscuitBrandsPage = lazy(() => import('./pages/BiscuitBrandsPage'));
const BiscuitsPage = lazy(() => import('./pages/BiscuitsPage'));
const SuppliersDirectory = lazy(() => import('./pages/SuppliersDirectory'));
const BlogList = lazy(() => import('./pages/BlogList'));
const BlogPost = lazy(() => import('./pages/BlogPost'));

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
    const { user, loading, isAuthenticated } = useAuth();
    const location = useLocation();
    
    if (loading) {
        return <LoadingSpinner fullScreen />;
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }
    
    if (requiredRole) {
        if (requiredRole === 'seller' && user?.role !== 'seller' && user?.role !== 'admin') {
            return <Navigate to="/" replace />;
        }
        if (requiredRole === 'admin' && user?.role !== 'admin') {
            return <Navigate to="/" replace />;
        }
    }
    
    return children;
};

import ProductRequestFAB from './components/common/ProductRequestFAB';

// Layout wrapper
const Layout = ({ children, hideFooter = false }) => {
    return (
        <div className="min-h-screen flex flex-col bg-background w-full max-w-full overflow-x-clip">
            <Navbar />
            <main className="flex-1 w-full max-w-full">
                {children}
            </main>
            {!hideFooter && <Footer />}
            <ProductRequestFAB />
        </div>
    );
};

// App Router with auth callback detection
const AppRouter = () => {
    const location = useLocation();
    
    // Check URL fragment for session_id synchronously during render
    if (location.hash?.includes('session_id=')) {
        return (
            <Suspense fallback={<LoadingSpinner fullScreen />}>
                <AuthCallback />
            </Suspense>
        );
    }
    
    return (
        <Suspense fallback={<LoadingSpinner fullScreen />}>
            <Canonical />
            <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Layout><Home /></Layout>} />
                <Route path="/shop" element={<Layout><Shop /></Layout>} />
                <Route path="/shop/:category" element={<Layout><Shop /></Layout>} />
                <Route path="/origins" element={<Layout><Origins /></Layout>} />
                <Route path="/featured-biscuits" element={<Layout><FeaturedBiscuits /></Layout>} />
                <Route path="/featured-shops" element={<Layout><FeaturedShops /></Layout>} />
                <Route path="/categories" element={<Layout><Categories /></Layout>} />
                <Route path="/new-arrivals" element={<Layout><NewArrivals /></Layout>} />
                <Route path="/sellers" element={<Layout><SellersByLocation /></Layout>} />
                <Route path="/product/:productId" element={<Layout><ProductDetail /></Layout>} />
                <Route path="/cart" element={<Layout><Cart /></Layout>} />
                <Route path="/login" element={<Layout><Login /></Layout>} />
                <Route path="/forgot-password" element={<Layout><ForgotPassword /></Layout>} />
                <Route path="/reset-password/:token" element={<Layout><ForgotPassword /></Layout>} />
                <Route path="/register" element={<Layout><Register /></Layout>} />
                <Route path="/verify-email" element={<Layout><VerifyEmail /></Layout>} />
                <Route path="/unsubscribe" element={<Layout><Unsubscribe /></Layout>} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                
                {/* Static Pages */}
                <Route path="/about" element={<Layout><StaticPage slug="about" /></Layout>} />
                <Route path="/contact" element={<Layout><StaticPage slug="contact" /></Layout>} />
                <Route path="/faq" element={<Layout><StaticPage slug="faq" /></Layout>} />
                <Route path="/terms" element={<Layout><StaticPage slug="terms" /></Layout>} />
                <Route path="/privacy" element={<Layout><StaticPage slug="privacy" /></Layout>} />
                <Route path="/returns" element={<Layout><StaticPage slug="returns" /></Layout>} />
                <Route path="/seller-policy" element={<Layout><StaticPage slug="seller-policy" /></Layout>} />
                <Route path="/become-a-seller" element={<Layout><BecomeASeller /></Layout>} />
                <Route path="/sell" element={<Layout><BecomeASeller /></Layout>} />
                <Route path="/awards" element={<Layout><AwardsPage /></Layout>} />
                
                {/* SEO Pillar Pages */}
                <Route path="/biscuit" element={<Layout><BiscuitPage /></Layout>} />
                <Route path="/biscuit-brands" element={<Layout><BiscuitBrandsPage /></Layout>} />
                <Route path="/biscuits" element={<Layout><BiscuitsPage /></Layout>} />
                
                {/* SEO Stakeholder Pages */}
                <Route path="/manufacturers" element={<Layout><StakeholderPage key="manufacturers" /></Layout>} />
                <Route path="/suppliers" element={<Layout><SuppliersDirectory /></Layout>} />
                <Route path="/bakers" element={<Layout><StakeholderPage key="bakers" /></Layout>} />
                <Route path="/buyers" element={<Layout><StakeholderPage key="buyers" /></Layout>} />
                <Route path="/lovers" element={<Layout><StakeholderPage key="lovers" /></Layout>} />
                <Route path="/wholesale-biscuits" element={<Layout><StakeholderPage key="wholesale-biscuits" /></Layout>} />
                <Route path="/artisan-biscuit-brands" element={<Layout><StakeholderPage key="artisan-biscuit-brands" /></Layout>} />
                <Route path="/gift-biscuit-brands" element={<Layout><StakeholderPage key="gift-biscuit-brands" /></Layout>} />
                
                {/* SEO Country Pages */}
                <Route path="/biscuits-by-country/:country" element={<Layout><BiscuitsByCountry /></Layout>} />
                
                {/* Blog */}
                <Route path="/blog" element={<Layout><BlogList /></Layout>} />
                <Route path="/blog/:slug" element={<Layout><BlogPost /></Layout>} />
                
                {/* SEO Aliases */}
                <Route path="/biscuit-suppliers" element={<Layout><SuppliersDirectory /></Layout>} />
                
                {/* Protected Routes - Buyer */}
                <Route path="/checkout" element={
                    <ProtectedRoute>
                        <Layout hideFooter><Checkout /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/checkout/success" element={
                    <Layout><CheckoutSuccess /></Layout>
                } />
                <Route path="/account/*" element={
                    <ProtectedRoute>
                        <Layout><BuyerDashboard /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/dashboard" element={
                    <ProtectedRoute>
                        <Layout><BuyerDashboard /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/wishlist" element={
                    <ProtectedRoute>
                        <Layout><Wishlist /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/review/:orderId" element={
                    <ProtectedRoute>
                        <Layout><ReviewOrder /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/messages" element={
                    <ProtectedRoute>
                        <Layout><Messages /></Layout>
                    </ProtectedRoute>
                } />
                
                {/* Protected Routes - Seller */}
                <Route path="/become-seller" element={
                    <ProtectedRoute>
                        <Layout><SellerRegister /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/seller/*" element={
                    <ProtectedRoute requiredRole="seller">
                        <Layout><SellerDashboard /></Layout>
                    </ProtectedRoute>
                } />
                
                {/* Public Seller Store Page (must be after /seller/*) */}
                <Route path="/store/:sellerId" element={<Layout><SellerStore /></Layout>} />
                
                {/* Protected Routes - Admin */}
                <Route path="/admin/*" element={
                    <ProtectedRoute requiredRole="admin">
                        <Layout><AdminDashboard /></Layout>
                    </ProtectedRoute>
                } />
                
                {/* 404 */}
                <Route path="*" element={
                    <Layout>
                        <div className="container-custom py-20 text-center">
                            <h1 className="text-4xl font-serif mb-4">Page Not Found</h1>
                            <p className="text-muted-foreground mb-8">The page you're looking for doesn't exist.</p>
                            <a href="/" className="text-primary hover:underline">Return Home</a>
                        </div>
                    </Layout>
                } />
            </Routes>
        </Suspense>
    );
};

function App() {
    // Global image error handler — catches ALL broken images via event delegation.
    // Replaces broken src with an inline SVG placeholder so no broken icons ever appear.
    useEffect(() => {
        const PRODUCT_FALLBACK = "data:image/svg+xml," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400"><rect fill="%23f5f0eb" width="400" height="400"/><circle cx="200" cy="170" r="60" fill="%23e0d5c8"/><circle cx="170" cy="160" r="6" fill="%23c4b5a4"/><circle cx="220" cy="155" r="5" fill="%23c4b5a4"/><circle cx="195" cy="185" r="7" fill="%23c4b5a4"/><circle cx="210" cy="140" r="4" fill="%23c4b5a4"/><text x="200" y="270" text-anchor="middle" fill="%23b8a99a" font-family="serif" font-size="18">Image unavailable</text></svg>');
        const BANNER_FALLBACK = "data:image/svg+xml," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 400"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="%23fef3c7"/><stop offset="100%" stop-color="%23fed7aa"/></linearGradient></defs><rect fill="url(%23g)" width="800" height="400"/><text x="400" y="210" text-anchor="middle" fill="%23b8860b" font-family="serif" font-size="28" opacity="0.5">Biscuit Brand</text></svg>');

        const handler = (e) => {
            if (e.target.tagName !== 'IMG') return;
            const img = e.target;
            if (img.dataset.fallbackApplied) return;
            img.dataset.fallbackApplied = 'true';

            // Check for a sibling fallback element (used by seller cards with initials)
            const sibling = img.parentElement?.querySelector('[data-role="img-fallback"]');
            if (sibling) {
                img.style.display = 'none';
                sibling.style.display = 'flex';
                return;
            }

            // Determine fallback by aspect ratio / size context
            const parent = img.parentElement;
            const isSquare = parent?.classList.contains('aspect-square');
            const isBanner = parent?.classList.contains('aspect-[16/9]') ||
                parent?.classList.contains('h-64') || parent?.classList.contains('h-80');

            img.src = isBanner ? BANNER_FALLBACK : PRODUCT_FALLBACK;
            img.style.objectFit = isSquare ? 'cover' : 'contain';
        };

        document.addEventListener('error', handler, true);
        return () => document.removeEventListener('error', handler, true);
    }, []);

    return (
        <BrowserRouter>
            <ScrollToTop />
            <AuthProvider>
                <CurrencyProvider>
                    <OrderAvailabilityProvider>
                    <CartProvider>
                        <WishlistProvider>
                            <AppRouter />
                            <LocationPermissionModal />
                            <CountrySelector />
                            <Toaster position="top-right" richColors />
                        </WishlistProvider>
                    </CartProvider>
                    </OrderAvailabilityProvider>
                </CurrencyProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
